/**
 * Auth0 in the browser.
 *
 * `@auth0/auth0-spa-js` is the only Auth0 SDK that works here — `nextjs-auth0`
 * and `express-openid-connect` depend on Node APIs that Cloudflare Workers do
 * not have, so the SPA flow is the shape that fits the deployment.
 *
 * The access token is what the API verifies. We never send the ID token to the
 * API; it is for display only.
 */

import { createAuth0Client, type Auth0Client, type User } from "@auth0/auth0-spa-js";

export const authConfig = {
  domain: import.meta.env.VITE_AUTH0_DOMAIN as string,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID as string,
  audience: import.meta.env.VITE_AUTH0_AUDIENCE as string,
};

function requireConfig(): void {
  const missing = Object.entries(authConfig)
    .filter(([, v]) => !v || v === "undefined")
    .map(([k]) => `VITE_AUTH0_${k.replace(/([A-Z])/g, "_$1").toUpperCase()}`);
  if (missing.length > 0) {
    throw new Error(
      `Auth0 is not configured. Missing ${missing.join(", ")} — check apps/web/.env and restart the dev server.`,
    );
  }
}

let clientPromise: Promise<Auth0Client> | null = null;

export function getAuthClient(): Promise<Auth0Client> {
  if (!clientPromise) {
    requireConfig();
    clientPromise = createAuth0Client({
      domain: authConfig.domain,
      clientId: authConfig.clientId,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: authConfig.audience,
      },
      // Survives a page refresh. Auth0's default in-memory cache does not, and
      // "logged out every time I hit reload" is the most common complaint about
      // SPA auth setups.
      cacheLocation: "localstorage",
      useRefreshTokens: true,
    });
  }
  return clientPromise;
}

export interface Session {
  user: User;
  token: string;
}

/**
 * Thrown when the only way to get a token is to go round through Auth0
 * again. The redirect has already been started when this is thrown; callers
 * should stop what they were doing and let the page leave.
 */
export class ReauthError extends Error {
  constructor() {
    super("Your session needs renewing — sending you back through sign-in.");
    this.name = "ReauthError";
  }
}

/**
 * Codes auth0-spa-js uses for "I cannot renew this in the background":
 * no refresh token was ever issued (the API does not allow offline access),
 * the refresh token was rejected or rotated away, or the Auth0 session that
 * would back a silent renewal has ended. In every case the answer is the
 * same: a full round trip to Auth0. If its session cookie is still alive
 * the user comes straight back without typing anything; if not, they sign
 * in, which is the truth of the situation.
 */
const NEEDS_LOGIN = new Set(["missing_refresh_token", "login_required", "consent_required", "invalid_grant", "interaction_required"]);

let reauthStarted = false;

/**
 * An access token, or a trip to Auth0 to get one.
 *
 * Access tokens for browser flows expire after two hours by default. Without
 * this, the first call after that threw "Missing Refresh Token" onto the
 * screen with nothing to click but Sign out.
 */
export async function tokenOrRelogin(client: Auth0Client): Promise<string> {
  try {
    // Newer auth0-spa-js types the result as possibly undefined; an empty
    // token is the same failure as a thrown one.
    const token = await client.getTokenSilently();
    if (!token) throw Object.assign(new Error("No access token"), { error: "missing_refresh_token" });
    return token;
  } catch (err) {
    const code = (err as { error?: string } | null)?.error ?? "";
    if (!NEEDS_LOGIN.has(code)) throw err;
    if (!reauthStarted) {
      reauthStarted = true;
      await client.loginWithRedirect();
    }
    throw new ReauthError();
  }
}

/**
 * Complete a redirect callback if we just came back from Auth0.
 *
 * Auth0 signals failure by redirecting BACK to us with ?error= rather than by
 * showing its own error page. Checking only for ?code silently discards that,
 * and the symptom is a login button that appears to do nothing.
 */
export async function handleRedirect(): Promise<void> {
  const params = new URLSearchParams(window.location.search);

  const error = params.get("error");
  if (error) {
    const description = params.get("error_description") ?? "";
    window.history.replaceState({}, document.title, window.location.pathname);
    throw new AuthSetupError(error, description);
  }

  if (!params.has("code") || !params.has("state")) return;

  const client = await getAuthClient();
  try {
    await client.handleRedirectCallback();
  } catch (err) {
    /**
     * An authorization code is single-use and its state is tied to a stored
     * transaction. Reloading the callback URL, going Back to it, or having
     * Safari restore the tab all replay a code that is already spent, and
     * auth0-spa-js reports that as "Invalid state".
     *
     * It is not an error worth showing. The session either exists already or
     * the user signs in again — one click either way. What DOES matter is
     * that the parameters get cleared regardless of outcome: stripping them
     * only on success (the original bug) turns one stale reload into a page
     * that throws forever, with no way out but editing the URL by hand.
     */
    const message = err instanceof Error ? err.message : String(err);
    if (!/invalid state/i.test(message)) throw err;
  } finally {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

/** An Auth0 tenant misconfiguration, reported back to us via the callback URL. */
export class AuthSetupError extends Error {
  constructor(readonly code: string, readonly description: string) {
    super(AuthSetupError.explain(code, description));
    this.name = "AuthSetupError";
  }

  /** Translate Auth0's codes into the specific thing to go fix. */
  private static explain(code: string, description: string): string {
    const detail = description ? ` Auth0 said: "${description}"` : "";
    switch (code) {
      case "access_denied":
        return `Auth0 refused the request. This usually means the API "${authConfig.audience}" does not exist — create it under Applications → APIs with that exact identifier.${detail}`;
      case "unauthorized_client":
        return `This application is not allowed to use this grant. Check the application type is Single Page Application.${detail}`;
      case "invalid_request":
        return `Auth0 rejected the request as malformed — most often an audience or callback URL mismatch.${detail}`;
      default:
        return `Auth0 returned "${code}".${detail}`;
    }
  }
}

export async function getSession(): Promise<Session | null> {
  const client = await getAuthClient();
  if (!(await client.isAuthenticated())) return null;
  const user = await client.getUser();
  if (!user) return null;
  const token = await tokenOrRelogin(client);
  return { user, token };
}

export async function login(): Promise<void> {
  const client = await getAuthClient();
  await client.loginWithRedirect();
}

export async function signup(): Promise<void> {
  const client = await getAuthClient();
  await client.loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
}

export async function logout(): Promise<void> {
  const client = await getAuthClient();
  await client.logout({ logoutParams: { returnTo: window.location.origin } });
}

/** Call the API with the access token attached. The API mints its own database token. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const client = await getAuthClient();
  const token = await tokenOrRelogin(client);
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    // Validation failures arrive as a list of messages already written for the
    // person. Carry the whole body through so a form can show all of them at
    // once instead of collapsing them into one line.
    // When the server sent no message of its own — a 404 from a route that
    // does not exist yet, a proxy error — name the status and the path.
    // "Not Found" alone tells you nothing about which call failed.
    const message =
      body.error ?? body.errors?.[0] ?? `${res.status} ${res.statusText} from ${path}`;
    // The paywall, met mid-session: tell the app so it re-reads /api/me and
    // puts the subscribe screen up, whatever screen made this call.
    if (res.status === 402 && body.code === "subscription_required") {
      window.dispatchEvent(new CustomEvent("jobcork:subscription-required"));
    }
    throw new ApiError(res.status, message, body);
  }
  // A 204 No Content (e.g. removing a saved answer) carries no body, so
  // res.json() would throw "Unexpected end of JSON input". Callers of such
  // routes ignore the return value.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Fetch a file the API builds for the user. A plain link cannot carry the
 * bearer token, so the bytes come back here and the caller hands them to the
 * browser as a download. The filename is whatever the server put in
 * Content-Disposition.
 */
export async function apiFile(path: string): Promise<{ blob: Blob; filename: string }> {
  const client = await getAuthClient();
  const token = await tokenOrRelogin(client);
  const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    const message = body.error ?? body.errors?.[0] ?? `${res.status} ${res.statusText} from ${path}`;
    throw new ApiError(res.status, message, body);
  }
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? "resume.docx" };
}

/** Hand a blob to the browser as a download, then release it. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface ApiErrorBody {
  error?: string;
  errors?: string[];
  code?: string;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body: ApiErrorBody = {}) {
    super(message);
    this.name = "ApiError";
  }

  /** Every message the server sent, or the single one it managed. */
  get messages(): string[] {
    return this.body.errors ?? [this.message];
  }
}
